/**
 * Gift cards.
 *
 * Selling a card is money in the drawer against goods that leave the shop some
 * other day, so it gets its own screen rather than sharing the basket with a
 * jumper: nothing is sold here, and the totals would have to be split down the
 * middle to pretend otherwise.
 *
 * The screen also answers the question the owner actually asks, which is not
 * "what did we sell in cards" but "how much do we still owe in goods" - the
 * balance of every card that has not been spent.
 */
import { useCallback, useEffect, useState } from 'react';
import { newId } from '@pos/shared';
import { useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { deviceId, takeReceiptNumber } from '../../core/device.js';
import { printHtml, escapeHtml, barcodeSvg } from '../../core/print.js';
import { Button, Empty, Field, Loading, Modal, MoneyInput, Pill } from '../../core/ui/ui.js';
import { PaymentDialog, type TakenPayment } from '../till/Payment.js';

interface Card {
  code: string;
  issued: number;
  used: number;
  remaining: number;
  expires_at: string | null;
  created_at: string;
  customer_name?: string | null;
}

export function GiftCards() {
  const t = useT();
  const { settings, money, date } = useSettings();
  const { can } = useAuth();
  const toast = useToast();

  const [q, setQ] = useState('');
  const [cards, setCards] = useState<Card[]>([]);
  const [outstanding, setOutstanding] = useState(0);
  const [liveCards, setLiveCards] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selling, setSelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api<{ cards: Card[]; outstanding: number; liveCards: number }>(
        `/exchange/giftcards?q=${encodeURIComponent(q)}`,
      );
      setCards(r.cards);
      setOutstanding(r.outstanding);
      setLiveCards(r.liveCards);
    } catch {
      toast.warn(t('net.needOnline'));
    } finally {
      setLoading(false);
    }
  }, [q, t, toast]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 200);
    return () => clearTimeout(timer);
  }, [load]);

  const statusOf = (c: Card) => {
    if (c.remaining <= 0) return <Pill>{t('gift.usedUp')}</Pill>;
    if (c.expires_at && new Date(c.expires_at) < new Date()) return <Pill kind="warn">{t('gift.expired')}</Pill>;
    return <Pill kind="info">{money(c.remaining)}</Pill>;
  };

  return (
    <div className="col">
      <div className="row wrap">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('gift.searchPlaceholder')} aria-label={t('gift.searchPlaceholder')}
          style={{ maxWidth: 360 }}
          data-scan-target="true"
        />
        <div className="spacer" />
        {can('giftcard.sell') ? (
          <Button variant="primary" onClick={() => setSelling(true)}>{t('gift.sell')}</Button>
        ) : null}
      </div>

      {/* What the shop owes in goods, which is the figure that matters. */}
      <div className="card">
        <div className="k">{t('gift.outstanding')}</div>
        <div className="v tnum big">{money(outstanding)}</div>
        <div className="small muted">
          {t('gift.liveCards', { n: liveCards })} · {t('gift.outstandingHint')}
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : cards.length === 0 ? (
        <Empty icon="🎁" title={t('gift.empty')} />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('gift.cardNumber')}</th>
                  <th>{t('gift.soldOn')}</th>
                  <th>{t('common.customer')}</th>
                  <th className="num">{t('gift.spent')}</th>
                  <th className="num">{t('gift.remaining')}</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c.code}>
                    <td className="strong tnum" data-label={t('gift.cardNumber')}>{c.code}</td>
                    <td data-label={t('gift.soldOn')}>{date(c.created_at)}</td>
                    <td data-label={t('common.customer')}>{c.customer_name ?? ''}</td>
                    <td className="num tnum" data-label={t('gift.spent')}>{money(c.used)}</td>
                    <td className="num" data-label={t('gift.remaining')}>{statusOf(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selling ? (
        <SellCard
          onClose={() => setSelling(false)}
          onSold={() => {
            setSelling(false);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

/** Amount, an optional card to activate, then the money. */
function SellCard({ onClose, onSold }: { onClose: () => void; onSold: () => void }) {
  const t = useT();
  const { settings, money } = useSettings();
  const toast = useToast();
  const [amount, setAmount] = useState(0);
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [paying, setPaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<{ code: string; amount: number } | null>(null);

  const sell = async (payments: TakenPayment[]) => {
    setSaving(true);
    const ticket = takeReceiptNumber();
    try {
      const r = await api<{ code: string; amount: number }>('/exchange/giftcards', {
        method: 'POST',
        // The till's own counter, exactly as a sale takes one, so the card
        // sale sits in the same numbered series and cannot collide with it.
        body: {
          id: newId(),
          receiptNo: ticket.receiptNo,
          deviceId: deviceId(),
          deviceCounter: ticket.counter,
          amount,
          code: code.trim() ? code.trim().toUpperCase() : null,
          expiresAt: expiresAt || null,
          payments,
        },
      });
      setPaying(false);
      setDone(r);
    } catch (e) {
      // A card that was not created must say so plainly: the money has not been
      // taken either, and the cashier has to know which of the two happened.
      setPaying(false);
      toast.warn(e instanceof Error ? e.message : t('error.generic'));
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Modal title={t('gift.sell')} onClose={onSold}>
        <div className="col center">
          <p className="strong">{t('gift.sold', { code: done.code, amount: money(done.amount) })}</p>
          <Button variant="primary" onClick={() => void printCard(done, settings, t)}>
            {t('gift.printCard')}
          </Button>
          <Button onClick={onSold}>{t('common.done')}</Button>
        </div>
      </Modal>
    );
  }

  if (paying) {
    return (
      <PaymentDialog
        total={amount}
        // A card cannot be bought with another card, with credit, or on the
        // slate: that would move a debt around rather than settle one.
        allowCredit={false}
        allowGift={false}
        allowTab={false}
        onCancel={() => setPaying(false)}
        onDone={(payments) => void sell(payments)}
      />
    );
  }

  return (
    <Modal
      title={t('gift.sell')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" disabled={amount <= 0 || saving} onClick={() => setPaying(true)}>
            {t('gift.takePayment')}
          </Button>
        </>
      }
    >
      <div className="col">
        <Field label={t('gift.amount')}>
          <MoneyInput className="big" value={amount} onValue={setAmount} decimals={2} autoFocus />
        </Field>
        <Field label={t('gift.cardNumber')} hint={t('gift.cardNumberHint')}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('gift.printedVoucher')}
            data-scan-target="true"
          />
        </Field>
        <Field label={t('gift.expiry')} hint={t('gift.expiryHint')}>
          <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </Field>
        <p className="small muted">{t('gift.notASale')}</p>
      </div>
    </Modal>
  );
}

/** The card itself, on the receipt printer: a number, a barcode and what it is worth. */
function printCard(
  card: { code: string; amount: number },
  settings: { storeNameEn: string; storeNameAr: string; receiptLang: 'en' | 'ar' | 'both' },
  t: (k: never) => string,
): Promise<void> {
  const ar = settings.receiptLang !== 'en';
  const en = settings.receiptLang !== 'ar';
  const name = ar ? settings.storeNameAr || settings.storeNameEn : settings.storeNameEn || settings.storeNameAr;
  const title = ar ? 'بطاقة هدية' : 'GIFT CARD';
  const note = ar
    ? 'قدّمها عند الصندوق. بتنصرف على أكثر من زيارة.'
    : 'Present this at the till. It can be spent over more than one visit.';
  const value = `${(card.amount / 100).toFixed(2)}`;
  return printHtml(
    `<style>
       body { width: 80mm; padding: 4mm; text-align: center; font-size: 12px; }
       .name { font-size: 15px; font-weight: 700; margin-bottom: 2mm; }
       .title { font-size: 17px; font-weight: 700; letter-spacing: 1px; border: 2px solid #000; padding: 2mm; margin: 2mm 0; }
       .value { font-size: 26px; font-weight: 700; margin: 3mm 0; }
       .note { font-size: 10px; margin-top: 3mm; }
     </style>
     <div class="name">${escapeHtml(name)}</div>
     <div class="title">${escapeHtml(title)}</div>
     <div class="value">₪${escapeHtml(value)}</div>
     ${barcodeSvg(card.code, { height: 40, width: 1.6, fontSize: 12, format: 'CODE128' })}
     <div class="note">${escapeHtml(note)}</div>
     ${en && ar ? `<div class="note">Present this at the till.</div>` : ''}`,
    { widthMm: 80, title: `Gift card ${card.code}` },
  );
}
